import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Send, RefreshCw, LifeBuoy, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

export function ContactForm({ dark = true }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [captcha, setCaptcha] = useState({ id: "", question: "" });
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const t = dark
    ? {
        section: "border-white/5 bg-[#05050A]",
        overline: "text-[#00F0FF]",
        heading: "text-white",
        body: "text-slate-400",
        infoCard: "border-white/10 bg-white/5 text-slate-200 hover:border-[#00F0FF]/40",
        infoSub: "text-slate-400",
        iconMail: "bg-[#00F0FF]/10 text-[#00F0FF]",
        iconLife: "bg-[#007AFF]/15 text-[#007AFF]",
        lifeText: "text-slate-400",
        formCard: "border-[#007AFF]/25 bg-[#0D0E15] shadow-[0_0_50px_rgba(0,122,255,0.12)]",
        label: "text-slate-300",
        input: "bg-[#05050A] border-white/10 text-white placeholder:text-slate-500",
        captchaVal: "text-[#00F0FF]",
        refresh: "text-slate-400 hover:text-white",
        submit: "bg-gradient-to-r from-[#00F0FF] to-[#007AFF] text-black hover:opacity-90",
        emailLink: "text-[#00F0FF]",
        muted: "text-slate-500",
        successIcon: "bg-[#00F0FF]/10 text-[#00F0FF]",
        successHeading: "text-white",
        successBody: "text-slate-400",
        outlineBtn: "border-white/15 text-white hover:bg-white/5",
      }
    : {
        section: "border-slate-200 bg-[#F6F8FC]",
        overline: "text-[#007AFF]",
        heading: "text-slate-900",
        body: "text-slate-600",
        infoCard: "border-slate-200 bg-white text-slate-800 hover:border-[#007AFF]/40",
        infoSub: "text-slate-500",
        iconMail: "bg-[#007AFF]/10 text-[#007AFF]",
        iconLife: "bg-[#007AFF]/10 text-[#007AFF]",
        lifeText: "text-slate-500",
        formCard: "border-slate-200 bg-white shadow-[0_10px_40px_rgba(15,23,42,0.06)]",
        label: "text-slate-700",
        input: "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400",
        captchaVal: "text-[#007AFF]",
        refresh: "text-slate-400 hover:text-slate-700",
        submit: "bg-gradient-to-r from-[#00F0FF] to-[#007AFF] text-black hover:opacity-90",
        emailLink: "text-[#007AFF]",
        muted: "text-slate-500",
        successIcon: "bg-[#007AFF]/10 text-[#007AFF]",
        successHeading: "text-slate-900",
        successBody: "text-slate-600",
        outlineBtn: "border-slate-300 text-slate-700 hover:bg-slate-50",
      };

  const loadCaptcha = useCallback(async () => {
    try {
      const { data } = await api.get("/contact/captcha");
      setCaptcha({ id: data.captcha_id, question: data.question });
      setAnswer("");
    } catch (e) { console.debug("captcha load failed", e); }
  }, []);

  useEffect(() => { loadCaptcha(); }, [loadCaptcha]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, captcha_id: captcha.id, captcha_answer: answer });
      setSent(true);
      toast.success("Thanks! Your message has been sent to our team.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your message.");
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className={`px-6 py-20 border-t ${t.section}`} data-testid="contact-section">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        <div>
          <div className={`text-xs font-bold uppercase tracking-[0.2em] ${t.overline}`}>Get in touch</div>
          <h2 className={`mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight ${t.heading}`}>
            Questions? We're here to help.
          </h2>
          <p className={`mt-4 leading-relaxed max-w-md ${t.body}`}>
            Send us a message and we'll get back to you shortly. For anything urgent — billing, account or
            technical help — email us directly.
          </p>
          <div className="mt-8 space-y-4">
            <a href="mailto:support@pnrdecode.com" data-testid="contact-email-link"
               className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${t.infoCard}`}>
              <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${t.iconMail}`}>
                <Mail className="h-5 w-5" />
              </span>
              <span>
                <span className={`block text-xs ${t.infoSub}`}>Help &amp; support</span>
                <span className="font-medium">support@pnrdecode.com</span>
              </span>
            </a>
            <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${t.infoCard}`}>
              <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${t.iconLife}`}>
                <LifeBuoy className="h-5 w-5" />
              </span>
              <span className={`text-sm ${t.lifeText}`}>We typically reply within one business day.</span>
            </div>
          </div>
        </div>

        <div className={`rounded-2xl border p-6 sm:p-8 ${t.formCard}`}>
          {sent ? (
            <div className="text-center py-10" data-testid="contact-success">
              <div className={`mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full ${t.successIcon}`}>
                <Send className="h-6 w-6" />
              </div>
              <h3 className={`font-heading text-xl font-bold ${t.successHeading}`}>Message sent!</h3>
              <p className={`mt-2 text-sm ${t.successBody}`}>Thanks for reaching out. We'll reply to you by email soon.</p>
              <Button data-testid="contact-send-another" onClick={() => setSent(false)} variant="outline" className={`mt-6 ${t.outlineBtn}`}>
                Send another message
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className={t.label}>Name</Label>
                  <Input data-testid="contact-name" value={form.name} onChange={set("name")} required placeholder="Your name"
                         className={`mt-1 ${t.input}`} />
                </div>
                <div>
                  <Label className={t.label}>Email</Label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} required placeholder="you@example.com"
                         className={`mt-1 ${t.input}`} />
                </div>
              </div>
              <div>
                <Label className={t.label}>Message</Label>
                <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} required rows={5} placeholder="How can we help?"
                          className={`mt-1 resize-none ${t.input}`} />
              </div>
              <div>
                <Label className={t.label}>Are you human? Solve: <span className={`font-pnr ${t.captchaVal}`} data-testid="contact-captcha-q">{captcha.question || "…"}</span></Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input data-testid="contact-captcha" value={answer} onChange={(e) => setAnswer(e.target.value.replace(/\D/g, ""))} required inputMode="numeric" placeholder="Answer"
                         className={`max-w-[140px] ${t.input}`} />
                  <Button type="button" data-testid="contact-captcha-refresh" onClick={loadCaptcha} variant="ghost" size="icon" className={t.refresh}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Button data-testid="contact-submit" type="submit" disabled={loading}
                      className={`w-full font-semibold ${t.submit}`}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send message</>}
              </Button>
              <p className={`text-xs text-center ${t.muted}`}>Or email us directly at <a href="mailto:support@pnrdecode.com" className={`${t.emailLink} hover:underline`}>support@pnrdecode.com</a></p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
